// 일일 패널(D3c, 설계도 work/blueprint ui-daily-board, 선택 시안 C28 images/daily-c1.webp): 마을 위쪽 가장자리 '일일' 버튼 → 오른쪽 약 53%에 남색 금테 게시판.
// 의뢰 3줄 · 던전 카드 2 · 무한의 탑 배너를 탭 없이 한 화면에. 왼쪽 퀘스트 카드·플레이 영역은 가리지 않는다.
// 의뢰 수령·던전/탑 입장은 모두 GameCommand(dailyClaim·enterDaily)로 게임에 보낸다 — 지갑 지급과 장면 전환은 GameSim이 한다. 이 패널은 장부를 읽어 보이기만 한다.
// 크기는 패널 폭 기준 cqw(디자인 폭 450px = 844×390 시안), 글자는 12px 아래로 내려가지 않는다.
import { DailyStore } from '../game/daily';
import type { AdventureView } from '../shared/adventure';
import type { GameCommand } from '../shared/types';
import { DUNGEON_INFO, TOWER_MAX, dayIndex } from '../shared/daily';
import { C, FONT, FONT_NUM, el } from './dom';
import { HUD_ICON_PX, iconOver } from './uiArt';
import { DAILY_ENTRY_READY, dungeonRows, questRows, resetText, towerView } from './dailyModel';
import { DAILY_OPEN_EVENT, DAILY_TOGGLE_EVENT } from './uiEvents';
import { liveView } from './liveopsModel';
import { livePill, renderLive } from './liveopsPanel';
import { menuBadges } from './menuBar';
import { liveMusic } from '../audio/bgm';
import { viewRect, viewW } from '../shared/viewport';
import { assetUrl } from '../pwa/assetMap';

const W = 450;
/** 디자인 px → 패널 폭 비례 길이 */
const q = (px: number): string => (px / W * 100).toFixed(2) + 'cqw';
/** 글자: 비례하되 12px 밑으로 내려가지 않는다 */
const f = (px: number): string => 'max(12px,' + q(px) + ')';
const dailyUrl = (name: string): string => assetUrl('ui/daily/' + name + '.webp');
const GOLD = '#F2C14E';
const FRAME = 'border:1.5px solid rgba(242,193,78,.65);border-radius:' + q(10) + ';background:linear-gradient(180deg,rgba(20,28,56,.96),rgba(11,16,34,.96));';
const PILL = 'display:inline-flex;align-items:center;gap:' + q(4) + ';padding:0 ' + q(8) + ';height:' + q(26) + ';min-height:22px;border-radius:999px;background:rgba(6,9,22,.8);border:1px solid rgba(242,193,78,.5);font:900 ' + f(13) + ' ' + FONT_NUM + ';color:#fff;white-space:nowrap;';

/** 이미지 에셋 + 실패 시 감추는 폴백(그라데이션 배경이 남는다) */
function icon(parent: HTMLElement, name: string, size: number, css = ''): HTMLImageElement {
  const im = document.createElement('img');
  im.src = dailyUrl(name);
  im.alt = '';
  im.draggable = false;
  im.style.cssText = 'width:' + q(size) + ';height:' + q(size) + ';object-fit:contain;flex:none;' + css;
  im.onerror = () => { im.style.visibility = 'hidden'; };
  parent.appendChild(im);
  return im;
}
/** 카드 배경 썸네일(뒤에 깔고 위에 어두운 막) */
function thumb(card: HTMLElement, name: string): void {
  const im = document.createElement('img');
  im.src = dailyUrl(name);
  im.alt = '';
  im.draggable = false;
  im.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:cover;z-index:0;';
  im.onerror = () => { im.style.display = 'none'; };
  card.appendChild(im);
  el('div', 'position:absolute;inset:0;z-index:1;background:linear-gradient(90deg,rgba(8,12,28,.82) 0%,rgba(8,12,28,.35) 70%,rgba(8,12,28,.15) 100%);', card);
}

export class DailyPanel {
  private readonly store: DailyStore;
  private readonly btn: HTMLButtonElement;
  private readonly box: HTMLDivElement;
  private readonly body: HTMLDivElement;
  private open = false;
  private shown = false;
  private town = 1;
  private renderKey = '';
  private pendingUntil = 0;
  /** 수령 직후: 게임이 장부를 쓴 뒤(다음 프레임)에 다시 그린다 */
  private rerender = false;
  /** 주간 이벤트 보기(C5): 의뢰·던전·탑 대신 이벤트 목록을 같은 자리에 그린다 */
  private showLive = false;

  constructor(parent: HTMLElement, private readonly send: (cmd: GameCommand) => void, now: () => number = Date.now, store?: DailyStore) {
    this.store = store ?? new DailyStore(now);
    this.now = now;
    // HUD 정리(2026-10-27): 마을 아이콘 줄(퀘스트·가방·영웅) 왼쪽에 붙는 둥근 달력 아이콘
    this.btn = el('button', 'position:absolute;right:256px;top:6px;width:44px;height:44px;padding:0;box-sizing:border-box;border-radius:50%;pointer-events:auto;cursor:pointer;display:none;align-items:center;justify-content:center;' +
      'color:#f4ecd8;background:rgba(10,16,32,.62);border:1px solid rgba(242,193,78,.65);z-index:3;', parent);
    // 그림: Codex image_gen 달력 아이콘(btn-daily). 받지 못하면 선 모양 달력이 대신 보인다(uiArt.iconOver)
    iconOver(el('div', 'width:' + HUD_ICON_PX + 'px;height:' + HUD_ICON_PX + 'px;display:grid;place-items:center;pointer-events:none;', this.btn), 'btn-daily', HUD_ICON_PX,
      '<svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></svg>');
    this.btn.setAttribute('aria-label', '일일');
    this.btn.setAttribute('data-tip', '일일 의뢰 · 던전 · 탑');
    this.btn.setAttribute('data-testid', 'daily-open');
    this.btn.addEventListener('click', () => { this.open = !this.open; this.render(); });
    // 2026-10-27: 여는 단추는 오른쪽 위 아이콘 메뉴 줄의 「일일」(menuBar.ts)로 옮겼다. 이 글자 단추는 숨겨 둔다(QA 호환)
    this.btn.style.visibility = 'hidden';
    window.addEventListener(DAILY_TOGGLE_EVENT, () => { if (!this.shown) return; this.open = !this.open; this.render(); });
    // 퀘스트 창의 「일일 의뢰 열기」
    // 설정(톱니) 창의 「의뢰 게시판」 바로가기는 일시정지를 풀면서 보낸다: 아직 숨은 상태면 잠깐 기다렸다가 마을 화면이 돌아오면 연다
    window.addEventListener(DAILY_OPEN_EVENT, () => { if (!this.shown) { this.pendingUntil = performance.now() + 1500; return; } this.open = true; this.render(); });
    this.box = el('div', 'position:absolute;right:1.4%;top:2.5%;bottom:2.5%;width:53%;display:none;flex-direction:column;gap:' + q(8) + ';padding:' + q(12) + ';box-sizing:border-box;container-type:size;overflow:hidden;pointer-events:auto;' +
      'background:linear-gradient(180deg,rgba(18,25,48,.97),rgba(10,14,30,.97));border:2px solid rgba(242,193,78,.75);border-radius:' + q(12) + ';font:600 ' + f(14) + ' ' + FONT + ';color:#fff;z-index:4;', parent);
    this.box.setAttribute('role', 'dialog');
    this.box.setAttribute('aria-label', '일일 콘텐츠');
    this.box.setAttribute('data-testid', 'daily-panel');
    this.body = el('div', '', this.box);
  }
  private readonly now: () => number;
  private frame = 0;
  private kRight = '';
  private lastAdvRev = -1;
  /** 현상금 진행을 못 넣었을 때 다시 시도할 프레임(0 = 없음) */
  private retryAdvAt = 0;

  /** 「일일」 단추 자리: 우상단 HUD(스테이지·시계·일시정지·미니맵 묶음) 왼쪽 바로 옆. 화면·배율이 달라도 그 묶음과 겹치지 않는다(1366x768에서 스테이지 이름을 가리던 고정 256px 대체) */
  private place(): void {
    // 마을 아이콘 줄(rpg.ts) 바로 왼쪽. 줄이 없으면(창이 열려 숨김) 그대로 둔다
    const row = document.querySelector('[data-testid="rpg-icon-row"]');
    const r = row ? viewRect(row) : null;
    if (!r || !r.width) return;
    const right = Math.round(viewW() - r.left + 6) + 'px';
    this.btn.style.top = Math.round(r.top) + 'px';
    const sz = Math.round(r.height) + 'px';
    if (this.btn.style.width !== sz) { this.btn.style.width = sz; this.btn.style.height = sz; }
    if (right !== this.kRight) { this.kRight = right; this.btn.style.right = right; }
  }

  /** 매 프레임 호출: 마을에서만 버튼을 보인다. 결과 화면으로 넘어온 처치 수를 한 판에 한 번만 일일 의뢰에 넣는다 */
  update(village: boolean, town: number, resultId: string, resultKills: number, resultDone: boolean, paused = false, adventure?: AdventureView): void {
    // 일시정지(톱니) 중에는 게시판을 닫는다(일시정지 창 위에 남아 가리던 문제). 받을 보상 점(menuBadges.daily)은 마을이면 계속 센다
    if (this.rerender) { this.rerender = false; this.render(); }
    const inVillage = village && !paused;
    this.town = town;
    if (village) this.frame++;
    if (inVillage && this.frame % 20 === 1) this.place();
    // 메뉴 줄 「일일」 빨간 점: 받을 수 있는 의뢰 보상이 있을 때(약 3초에 한 번만 장부를 본다). 마을 첫 프레임에도 한 번 봐서 이벤트 곡이 마을 곡보다 늦게 나오지 않게 한다
    if (village && (this.frame === 1 || this.frame % 180 === 2)) { const ds = this.store.refresh(); const lv = liveView(ds, Math.max(1, town)); menuBadges.daily = questRows(ds, Math.max(1, town)).some((r) => r.claimable) || lv.dot; liveMusic.key = lv.bgm; }
    else if (!village) menuBadges.daily = false;
    if (inVillage && !this.shown && this.pendingUntil > performance.now()) { this.pendingUntil = 0; this.open = true; }
    if (inVillage !== this.shown) { this.shown = inVillage; this.btn.style.display = inVillage ? 'flex' : 'none'; if (!inVillage) { this.open = false; } this.render(); }
    // (처치 수 의뢰 「오늘의 사냥」은 「오늘의 현상금」으로 바뀌어 처치 수 연산을 더 넣지 않는다 — 아무 의뢰도 올리지 않는 연산이 장부 로그 200칸을 채우지 않게, PR #248 리뷰 P2)
    // 오늘의 현상금: 모험 목표판에서 오늘 완료한 현상금 하나당 진행 1. 연산 id가 현상금 id라 장부가 중복을 거른다
    if (village && adventure && (adventure.revision !== this.lastAdvRev || (this.retryAdvAt > 0 && this.frame >= this.retryAdvAt))) {
      this.retryAdvAt = 0;
      this.lastAdvRev = adventure.revision;
      const day = this.store.refresh().day;
      let any = false;
      let retry = false;
      for (const bid of adventure.done) {
        if (Number(bid.split(':')[0]) !== day) continue;
        const code = this.store.apply({ id: 'bty-' + bid, t: 'questProgress', goal: 'bounty', n: 1 }, Math.max(1, town)).code;
        if (code === 'ok') any = true;
        else if (code !== 'duplicate') retry = true;
      }
      // 장부가 잠시 읽기 전용(다른 탭·읽기 오류)이라 못 넣었으면 다음에 다시 시도한다(약 3초 뒤, 매 프레임 쓰기 시도 금지)
      if (retry) this.retryAdvAt = this.frame + 180;
      if (any && this.open) this.render();
    }
    // 열어 둔 패널: 날짜가 바뀌거나 표시 분이 바뀌면 다시 그린다
    if (this.open && this.shown) {
      const t = this.now();
      const key = Math.floor(t / 60000) + ':' + dayIndex(t);
      if (key !== this.renderKey) this.render();
    }
  }

  /** 카드·배너를 눌러 입장하는 진짜 버튼으로 만든다(role=button, Enter/Space, 44px 이상). 누르면 입장 명령을 보내고 패널을 닫는다 */
  private makeEnter(node: HTMLElement, label: string, kind: 'vault' | 'mine' | 'tower'): void {
    node.setAttribute('role', 'button');
    node.setAttribute('tabindex', '0');
    node.setAttribute('aria-label', label);
    node.style.cursor = 'pointer';
    if (kind !== 'tower') node.style.minHeight = '44px';
    let busy = false;
    const go = (): void => {
      if (busy) return;
      busy = true;
      this.send({ type: 'enterDaily', kind });
      this.open = false;
      this.render();
    };
    node.addEventListener('click', go);
    node.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(); } });
  }

  private render(): void {
    const show = this.open && this.shown;
    menuBadges.dailyOpen = show;
    this.box.style.display = show ? 'flex' : 'none';
    if (!show) return;
    const s = this.store.refresh();
    // 카운트다운·날짜 키는 장부가 쓰는 보정 시각(시계를 되돌려도 본 최대 시각)으로
    const eff = Math.max(this.now(), s.seenMs);
    this.renderKey = Math.floor(this.now() / 60000) + ':' + dayIndex(this.now());
    const t = Math.max(1, this.town);
    const b = this.body;
    b.textContent = '';
    b.style.cssText = 'display:flex;flex-direction:column;gap:' + q(8) + ';flex:1;min-height:0;';

    // 머리줄: 금색 별 + 제목 + 초기화 알약 + 닫기(44px 이상)
    const head = el('div', 'display:flex;align-items:center;gap:' + q(10) + ';flex:none;', b);
    el('div', 'width:' + q(30) + ';min-width:24px;aspect-ratio:1;border-radius:50%;border:1.5px solid #c99a3a;background:radial-gradient(circle at 40% 35%,#ffe9a0,#c98a24);color:#2a1d05;font:900 ' + f(16) + ' ' + FONT + ';display:flex;align-items:center;justify-content:center;', head, '✦');
    el('div', 'font:900 ' + f(21) + ' ' + FONT + ';color:' + GOLD + ';text-shadow:0 2px 4px #000;white-space:nowrap;', head, '일일 콘텐츠');
    const lv = liveView(s, t);
    if (!lv.active) this.showLive = false;
    el('div', PILL + 'margin-left:auto;font-family:' + FONT + ';', head, '초기화까지 ' + resetText(eff));
    if (lv.active) livePill(head, lv, this.showLive, () => { this.showLive = !this.showLive; this.render(); });
    const close = el('button', 'flex:none;min-width:44px;min-height:44px;width:' + q(40) + ';height:' + q(40) + ';border-radius:' + q(8) + ';cursor:pointer;font:900 ' + f(18) + ' ' + FONT + ';color:' + GOLD + ';background:#1A2238;border:1.5px solid rgba(242,193,78,.6);', head, '✕');
    close.setAttribute('aria-label', '닫기');
    close.setAttribute('data-testid', 'daily-close');
    close.addEventListener('click', () => { this.open = false; this.render(); });

    // 주간 이벤트 보기: 같은 자리에 이벤트 목록(수령은 GameCommand, 다음 프레임에 새 장부로 다시 그림)
    if (this.showLive && lv.active) {
      renderLive(b, lv, this.send, () => { this.rerender = true; });
      return;
    }

    // 일일 의뢰 3줄
    const list = el('div', FRAME + 'display:flex;flex-direction:column;flex:0 0 ' + q(150) + ';padding:' + q(2) + ' ' + q(8) + ';', b);
    for (const r of questRows(s, t)) {
      const row = el('div', 'display:flex;align-items:center;gap:' + q(8) + ';flex:1;min-height:0;' + (r === questRows(s, t)[0] ? '' : 'border-top:1px solid rgba(242,193,78,.18);') + (r.locked ? 'opacity:.55;' : ''), list);
      const ic = el('div', 'flex:none;width:' + q(34) + ';aspect-ratio:1;border-radius:50%;border:1.5px solid #c99a3a;background:radial-gradient(circle,#26335a,#121a33);display:flex;align-items:center;justify-content:center;', row);
      icon(ic, r.icon, 26);
      const mid = el('div', 'flex:1;min-width:0;', row);
      el('div', 'font:900 ' + f(16) + ' ' + FONT + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', mid, r.name + (r.locked || r.claimed || r.hint ? ' · ' + r.text : ' ' + r.progress + ' / ' + r.goal));
      const bar = el('div', 'height:' + q(6) + ';min-height:4px;margin-top:' + q(3) + ';border-radius:99px;background:rgba(255,255,255,.12);overflow:hidden;', mid);
      el('div', 'height:100%;width:' + Math.round(r.progress / r.goal * 100) + '%;background:linear-gradient(90deg,#f2c14e,#ffe27a);', bar);
      if (r.hint && !r.locked && !r.claimed) el('div', 'font:600 ' + f(11) + ' ' + FONT + ';color:rgba(255,255,255,.62);margin-top:' + q(2) + ';white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', mid, r.hint);
      const gp = el('span', PILL, row); icon(gp, 'coin', 16); el('span', '', gp, r.gold.toLocaleString('ko-KR'));
      if (r.stones) { const sp = el('span', PILL, row); icon(sp, 'stone', 16); el('span', '', sp, String(r.stones)); }
      if (r.claimable) {
        const claim = el('button', 'flex:none;min-width:44px;min-height:44px;padding:0 ' + q(12) + ';border-radius:' + q(8) + ';cursor:pointer;font:900 ' + f(15) + ' ' + FONT + ';color:#2a1d05;background:linear-gradient(#ffd877,#e0a52f);border:0;box-shadow:0 2px 0 rgba(0,0,0,.45);', row, '수령');
        claim.setAttribute('data-testid', 'daily-claim-' + r.id);
        claim.addEventListener('click', () => {
          // 연타 방지: 누르면 바로 비활성, 다음 프레임 재그림에서 새 장부(수령 완료)로 바뀐다
          if (claim.disabled) return;
          claim.disabled = true;
          claim.style.opacity = '.5';
          this.send({ type: 'dailyClaim', quest: r.id });
          this.rerender = true;
        });
      } else if (r.claimed) {
        el('div', 'flex:none;min-width:44px;text-align:center;font:900 ' + f(13) + ' ' + FONT + ';color:#7ff0a8;', row, '✔');
      }
    }

    // 일일 던전 2카드(가로)
    const dg = el('div', 'display:flex;gap:' + q(8) + ';flex:0 0 ' + q(60) + ';', b);
    for (const d of dungeonRows(s, t)) {
      const card = el('div', FRAME + 'position:relative;overflow:hidden;flex:1;min-width:0;display:flex;align-items:center;padding:0 ' + q(10) + ';' + (d.locked ? 'filter:saturate(.5);' : ''), dg);
      card.setAttribute('data-testid', 'daily-dungeon-' + d.kind);
      thumb(card, d.kind === 'vault' ? 'vault' : 'mine');
      const tx = el('div', 'position:relative;z-index:2;flex:1;min-width:0;', card);
      el('div', 'font:900 ' + f(16) + ' ' + FONT + ';text-shadow:0 2px 4px #000;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;', tx, DUNGEON_INFO[d.kind].name);
      el('div', 'font:700 ' + f(13) + ' ' + FONT + ';color:#e9d8b4;text-shadow:0 1px 3px #000;', tx, d.text);
      el('div', 'position:relative;z-index:2;flex:none;width:' + q(26) + ';min-width:20px;aspect-ratio:1;border-radius:50%;border:1.5px solid rgba(242,193,78,.8);background:rgba(8,12,28,.7);color:' + GOLD + ';display:flex;align-items:center;justify-content:center;font:900 ' + f(14) + ' ' + FONT + ';' + (d.locked || !d.enterable ? 'opacity:.45;' : ''), card, d.locked ? '🔒' : '›');
      if (d.enterable) this.makeEnter(card, '일일 던전 입장: ' + DUNGEON_INFO[d.kind].name, d.kind);
      else card.title = d.locked ? d.text : !DAILY_ENTRY_READY ? '입구 준비 중' : '오늘 횟수를 모두 썼어요';
    }

    // 무한의 탑 배너
    const tw = towerView(s, t);
    const tower = el('div', FRAME + 'position:relative;overflow:hidden;flex:1 1 0;min-height:' + q(86) + ';display:flex;flex-direction:column;justify-content:space-between;padding:' + q(7) + ' ' + q(10) + ';' + (tw.locked ? 'filter:saturate(.5);' : ''), b);
    tower.setAttribute('data-testid', 'daily-tower');
    if (tw.enterable) this.makeEnter(tower, '무한의 탑 입장', 'tower');
    else if (!tw.locked) tower.title = tw.next ? '오늘 도전을 이미 했어요' : '1~' + TOWER_MAX + '층 정복';
    thumb(tower, 'tower');
    const top = el('div', 'position:relative;z-index:2;display:flex;align-items:center;gap:' + q(8) + ';', tower);
    el('div', 'font:900 ' + f(17) + ' ' + FONT + ';color:' + GOLD + ';text-shadow:0 2px 4px #000;white-space:nowrap;', top, '무한의 탑');
    if (tw.locked) el('div', 'font:700 ' + f(13) + ' ' + FONT + ';', top, tw.text);
    else {
      const bx = (label: string, v: string): void => { const o = el('div', 'padding:0 ' + q(8) + ';border-radius:' + q(6) + ';background:rgba(6,9,22,.78);border:1px solid rgba(242,193,78,.4);text-align:center;line-height:1.15;white-space:nowrap;', top); el('div', 'font:600 ' + f(11) + ' ' + FONT + ';color:#cdbb8a;', o, label); el('div', 'font:900 ' + f(15) + ' ' + FONT + ';', o, v); };
      bx('최고 기록', tw.best + '층');
      if (tw.next) bx('다음 목표', tw.next + '층');
      if (tw.boss) el('div', 'font:900 ' + f(12) + ' ' + FONT + ';color:#ff8a8a;', top, tw.next + '층 보스');
      const st = el('div', PILL + 'margin-left:auto;font-family:' + FONT + ';color:' + (tw.enterable ? '#7ff0a8' : '#9aa2bc') + ';', top, tw.stateLabel);
      st.setAttribute('data-testid', 'daily-tower-state');
    }
    const strip = el('div', 'position:relative;z-index:2;display:flex;align-items:center;gap:' + q(8) + ';', tower);
    el('div', 'font:900 ' + f(13) + ' ' + FONT_NUM + ';', strip, tw.best + ' / ' + TOWER_MAX);
    const track = el('div', 'position:relative;flex:1;height:' + q(6) + ';min-height:4px;border-radius:99px;background:rgba(255,255,255,.16);', strip);
    el('div', 'height:100%;width:' + Math.round(tw.ratio * 100) + '%;border-radius:99px;background:linear-gradient(90deg,#f2c14e,#ffe27a);', track);
    for (let m = 10; m <= TOWER_MAX; m += 10) el('div', 'position:absolute;top:50%;left:' + (m / TOWER_MAX * 100) + '%;width:' + q(7) + ';min-width:5px;aspect-ratio:1;transform:translate(-50%,-50%) rotate(45deg);background:' + (tw.best >= m ? '#ffe27a' : '#6f7794') + ';', track);

    if (s.granted.gems > 0) el('div', 'flex:none;font:600 ' + f(12) + ' ' + FONT + ';color:#cdbb8a;', b, '탑 젬 ' + s.granted.gems + '개 보관 중 — 서버 지급 연결 뒤 받습니다');
    if (this.store.unsaved) el('div', 'flex:none;font:600 ' + f(11) + ' ' + FONT + ';color:#ffb45e;', b, '저장하지 못했습니다(저장소 차단·가득 참). 다시 시도 중이며, 이 상태로 닫으면 사라질 수 있습니다.');
    if (this.store.readFailed) el('div', 'flex:none;font:600 ' + f(11) + ' ' + FONT + ';color:#ffb45e;', b, '저장소를 읽지 못해 일일 기록을 쓰지 않습니다(읽을 수 있게 되면 이어집니다).');
    if (this.store.readOnly) el('div', 'flex:none;font:600 ' + f(11) + ' ' + FONT + ';color:#ffb45e;', b, '더 새 버전의 일일 기록이 있어 읽기만 합니다(덮어쓰지 않음).');
    if (this.store.corrupted) el('div', 'flex:none;font:600 ' + f(11) + ' ' + FONT + ';color:#ffb45e;', b, '이전 일일 기록이 손상돼 새로 시작했습니다(원문은 보존).');
  }
}
